import React, { createContext, useContext, useReducer, useCallback } from "react";
import ToastContainer from "../components/ui/ToastContainer";

const ToastContext = createContext(null);

const MAX_TOASTS = 5;
const DURATIONS = { success: 4000, info: 4000, warning: 4000, error: 6000 };

let toastCounter = 0;

function reducer(state, action) {
    switch (action.type) {
        case "ADD": {
            const next = [...state, action.toast];
            return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next;
        }
        case "REMOVE":
            return state.filter((t) => t.id !== action.id);
        default:
            return state;
    }
}

export const ToastProvider = ({ children }) => {
    const [toasts, dispatch] = useReducer(reducer, []);

    const removeToast = useCallback((id) => {
        dispatch({ type: "REMOVE", id });
    }, []);

    const addToast = useCallback((type, message, options = {}) => {
        const id = `toast-${++toastCounter}`;
        const duration = options.duration || DURATIONS[type] || 4000;
        dispatch({ type: "ADD", toast: { id, type, message, duration } });
    }, []);

    const api = useCallback(() => {}, []);
    api.success = useCallback((msg, opts) => addToast("success", msg, opts), [addToast]);
    api.error   = useCallback((msg, opts) => addToast("error", msg, opts), [addToast]);
    api.warning = useCallback((msg, opts) => addToast("warning", msg, opts), [addToast]);
    api.info    = useCallback((msg, opts) => addToast("info", msg, opts), [addToast]);

    const value = React.useMemo(() => ({
        success: api.success,
        error: api.error,
        warning: api.warning,
        info: api.info,
    }), [api.success, api.error, api.warning, api.info]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error("useToast must be used within a ToastProvider");
    return ctx;
};
